// components/Hero.jsx
import React, { useState, useEffect, useRef } from 'react';
import '../assets/css/Hero.css';

// Import your video directly
import heroVideo from '../assets/vids/tst2.mp4';

// Import logo PNG files
import googleWorkspaceLogo from '../assets/tools/gws.png';
import notionLogo from '../assets/tools/notion.png';
import hubstaffLogo from '../assets/tools/hubstaff.png';
import canvaLogo from '../assets/tools/canva.png';
import slackLogo from '../assets/tools/Slack-Logo.png';
import wiseLogo from '../assets/tools/01-Wise-logo-bright-green.png';
import gohighlevel from '../assets/tools/gohigh.jpg';

// Import ConsultationScheduling component
import ConsultationScheduling from './ConsultationScheduling';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [hasRequestedConsultation, setHasRequestedConsultation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const slideIntervalRef = useRef(null);
  
  // Enhanced slides with premium content
  const slides = [
    { 
      title: 'Stalled Filipino Professionals',
      description: 'Unlock their potential with our premium virtual assistant solutions',
      caption: 'Elite Talent Pool',
      highlight: true
    },
    { 
      title: 'Premium Virtual Assistant Solutions',
      description: 'Empowering global businesses with top-tier Filipino professionals',
      caption: 'Professional Virtual Assistants',
      highlight: false
    },
    { 
      title: 'The Future of Work is Remote',
      description: 'Build your elite dream team with our dedicated virtual assistants',
      caption: 'Skilled Filipino Professionals',
      highlight: false
    },
    { 
      title: 'Global Workforce Solutions',
      description: 'Seamlessly Find, Onboard, and Scale Your Remote Dream Team',
      caption: 'Global Business Support',
      highlight: false
    }
  ];

  // Premium brand logos data is defined inside BrandLogos component
  // const brandLogos = [
  //   { name: 'Google Workspace', logo: googleWorkspaceLogo, tier: 'enterprise' },
  //   { name: 'Notion', logo: notionLogo, tier: 'premium' },
  //   { name: 'Hubstaff', logo: hubstaffLogo, tier: 'professional' },
  //   { name: 'Canva', logo: canvaLogo, tier: 'pro' },
  //   { name: 'Slack', logo: slackLogo, tier: 'enterprise' },
  //   { name: 'Wise', logo: wiseLogo, tier: 'business' },
  //   { name: 'Gohighlevel', logo: gohighlevel, tier: 'business' }
  // ];

  // Auto-slide functionality
  useEffect(() => {
    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current);
    }
    
    if (!isPaused) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
    }
    
    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [isPaused, slides.length]);

  // Check if user has already requested consultation (prevent duplicates)
  useEffect(() => {
    const requested = localStorage.getItem('hasRequestedConsultation');
    if (requested) {
      setHasRequestedConsultation(true);
    }
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    restartAutoSlide();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    restartAutoSlide();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    restartAutoSlide();
  };

  const pauseSlider = () => setIsPaused(true);
  const resumeSlider = () => setIsPaused(false);

  const restartAutoSlide = () => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 100);
  };

  // Handle Get Started button click
  const handleGetStarted = () => {
    if (hasRequestedConsultation) {
      alert('You have already requested a consultation. We will contact you soon!');
      return;
    }
    setIsSubmitting(true);
    setShowConsultationModal(true);
    setTimeout(() => setIsSubmitting(false), 1000);
  };

  // Handle consultation success (currently handled internally by ConsultationScheduling)
  // const handleConsultationSuccess = () => {
  //   setHasRequestedConsultation(true);
  //   localStorage.setItem('hasRequestedConsultation', 'true');
  //   setShowConsultationModal(false);
  // };

  // Handle modal close
  const handleModalClose = () => {
    setShowConsultationModal(false);
  };

  // Premium BrandLogos component
  const BrandLogos = () => {
    const brandLogos = [
      { name: 'Google Workspace', logo: googleWorkspaceLogo, className: 'google-workspace' },
      { name: 'Notion', logo: notionLogo, className: 'notion' },
      { name: 'Hubstaff', logo: hubstaffLogo, className: 'hubstaff' },
      { name: 'Canva', logo: canvaLogo, className: 'canva' },
      { name: 'Slack', logo: slackLogo, className: 'slack' },
      { name: 'Wise', logo: wiseLogo, className: 'wise' },
      { name: 'Gohighlevel', logo: gohighlevel, className: 'gohigh' }
    ];

    return (
      <div className="brand-logos-section">
        <div className="brand-logos-container">
          <div className="powered-by-header">
            <span className="trusted-by">TRUSTED BY INDUSTRY LEADERS</span>
          </div>
          
          <div className="logos-grid">
            {brandLogos.map((brand) => (
              <div 
                key={brand.name} 
                className={`logo-item ${brand.className}`}
                data-logo={brand.className}
              >
                <img 
                  src={brand.logo} 
                  alt={brand.name} 
                  className="brand-logo"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
          
          <div className="integration-text">
            <p>Seamlessly integrated with the tools that power modern businesses</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <section id="home" className="hero">
        <div className="hero-image">
          <div 
            className="slider-container"
            onMouseEnter={pauseSlider}
            onMouseLeave={resumeSlider}
          >
            {/* Video Background */}
            <video 
              autoPlay 
              muted 
              loop 
              playsInline 
              className="hero-video"
            >
              <source src={heroVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Enhanced Text Slides */}
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`slide-content ${index === currentSlide ? 'active' : ''} ${
                  slide.highlight ? 'highlight-slide' : ''
                }`}
              >
                <div className="content-wrapper">
                  {slide.highlight && (
                    <div className="premium-badge">Premium Solution</div>
                  )}
                  <h1>{slide.title}</h1>
                  <p>{slide.description}</p>
                  <div className="slide-caption">{slide.caption}</div>
                  <div className="hero-buttons">
                    <button 
                      className={`btn-primary ${hasRequestedConsultation ? 'applied' : ''} ${isSubmitting ? 'loading' : ''}`}
                      onClick={handleGetStarted}
                      disabled={hasRequestedConsultation || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          Processing...
                        </>
                      ) : hasRequestedConsultation ? (
                        'Consultation Requested!'
                      ) : (
                        <>
                          Get Started
                          <span className="btn-arrow"></span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Enhanced Navigation Arrows */}
            <button 
              className="slider-arrow prev-arrow" 
              onClick={prevSlide}
              aria-label="Previous slide"
            >
              <span className="arrow-icon">‹</span>
            </button>
            <button 
              className="slider-arrow next-arrow" 
              onClick={nextSlide}
              aria-label="Next slide"
            >
              <span className="arrow-icon">›</span>
            </button>
            
            {/* Premium Dots Indicator */}
            <div className="slider-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span className="dot-progress"></span>
                </button>
              ))}
            </div>

            {/* Auto-play Indicator */}
            <div className="auto-play-indicator">
              <div className={`auto-play-progress ${isPaused ? 'paused' : ''}`}></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Premium Brand Logos Section */}
      <BrandLogos />

      {/* Consultation Scheduling Modal */}
      {showConsultationModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button 
              className="close-button" 
              onClick={handleModalClose}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#64748b'
              }}
            >
              ×
            </button>
            <ConsultationScheduling />
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;